import React, { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import CONFIG from '../../config/config';
import { useTranslation } from 'react-i18next';
import OnboardingAPI from '../../api/OnboardingAPI';
import { toastService } from '../../services/ToastService';
import useOnboardingQueryPresets, { OnboardingStep as OnboardingQueryStep,} from "../../hooks/useOnboardingQueryPresets";
import OnboardingWizardLayout, { OnboardingLanguageSwitcher } from "./OnboardingWizardLayout";
import { ONBOARDING_BRAND, onboardingChoiceSurface } from "./onboardingBrand";
import { getOnboardingNextPath } from "../../utils/onboardingFirstWin";

interface ChoiceOption {
  value: string;
  label: string;
}

/**
 * Converts answers (which may contain string[] for multiple-choice) to a flat
 * Record<string, string> for the backend. Arrays are joined with comma.
 */
export function normalizeAnswersForApi(answers: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(answers)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      result[key] = value;
    } else if (Array.isArray(value)) {
      result[key] = value.map((v) => (typeof v === "string" ? v : String(v))).join(",");
    } else {
      result[key] = String(value);
    }
  }
  return result;
}

const OnboardingFlow: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";

  const { steps: filteredSteps, presetAnswers } = useOnboardingQueryPresets(CONFIG.ONBOARDING_QUESTIONS as OnboardingQueryStep[], location.search);
  const steps = filteredSteps as OnboardingQueryStep[];
  
  const [stepIndex, setStepIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedSingle, setSelectedSingle] = useState<string | undefined>();
  const [answers, setAnswers] = useState<Record<string, any>>(() => ({ ...presetAnswers, }));
  const [loading, setLoading] = useState(false);

  const endQuestionnaire = useCallback(async (finalAnswers = answers) => {
    setFade(false);
    setLoading(true);
    try {
      await OnboardingAPI.saveAnswers(normalizeAnswersForApi(finalAnswers));
    } catch (e) {
      toastService.showToast(t('global.error.instructions') || 'An error occurred while saving your answers.', 'error');
    } finally {
      setTimeout(() => {
        setLoading(false);
        const nextPath = getOnboardingNextPath(finalAnswers.first_win);
        navigate(nextPath, { state: { from: redirectTo, firstWin: finalAnswers.first_win } });
      }, 500);
    }
  }, [answers, navigate, redirectTo, t]);

  const getNextUnansweredIndex = useCallback(
    (currentIdx: number, currentAnswers: Record<string, any>) => {
      for (let idx = currentIdx + 1; idx < steps.length; idx += 1) {
        const step = steps[idx];
        if (currentAnswers[step.answerKey] === undefined) {
          return idx;
        }
      }
      return null;
    },
    [steps]
  );

  const goToNextAvailableStep = useCallback(
    (currentIdx: number, currentAnswers: Record<string, any>) => {
      const nextIndex = getNextUnansweredIndex(currentIdx, currentAnswers);
      if (nextIndex === null) {
        endQuestionnaire(currentAnswers);
        return;
      }
      setFade(false);
      setTimeout(() => {
        setStepIndex(nextIndex);
        setFade(true);
        setSelectedOptions([]);
        setSelectedSingle(undefined);
      }, 500);
    },
    [endQuestionnaire, getNextUnansweredIndex]
  );

  const handleNext = () => {
    const currentStep = steps[stepIndex];
    if (!currentStep) return;

    let updatedAnswers: Record<string, any> = answers;
    if (currentStep.type === 'multiple-choice') {
      const existingAnswer = answers[currentStep.answerKey];
      if (existingAnswer === undefined) {
        updatedAnswers = { ...answers, [currentStep.answerKey]: selectedOptions };
        setAnswers(updatedAnswers);
      }
    }
    if (currentStep.type === 'single-choice' && selectedSingle) {
      const existingAnswer = answers[currentStep.answerKey];
      if (existingAnswer === undefined) {
        updatedAnswers = { ...answers, [currentStep.answerKey]: selectedSingle };
        setAnswers(updatedAnswers);
      }
    }

    goToNextAvailableStep(stepIndex, updatedAnswers);
  };

  const handleAnswer = (answer: string) => {
    const currentStep = steps[stepIndex];
    if (!currentStep) return;

    let updatedAnswers: Record<string, any> = answers;
    const existingAnswer = answers[currentStep.answerKey];
    if (existingAnswer === undefined) {
      updatedAnswers = { ...answers, [currentStep.answerKey]: answer };
      setAnswers(updatedAnswers);
    }

    goToNextAvailableStep(stepIndex, updatedAnswers);
  };

  const handleOptionChange = (value: string) => {
    setSelectedOptions((prev) =>
      prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]
    );
  };

  const currentStep = steps[stepIndex];
  if (!currentStep) return null;
  const choiceOptions = currentStep.options as ChoiceOption[] | undefined;

  return (
    <OnboardingWizardLayout
      fade={fade}
      loading={loading}
      loadingText={t('global.loading') || 'Saving...'}
      title={t(currentStep.title || '') as string}
      subtitle={currentStep.type === 'intro' ? t('onboarding.intro.subtitle') as string : undefined}
      showProgress
      progressTotal={steps.length}
      progressIndex={stepIndex}
    >
      {currentStep.type === 'intro' && (
        <div className="flex flex-col items-center space-y-8">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
            {t(currentStep.description || '') as string}
          </p>
          <PrimaryButton
            tone="brand"
            text={t(currentStep.primaryButton || '') as string}
            fn={handleNext}
            actionName="onboarding_intro_next"
          />
          <OnboardingLanguageSwitcher />
        </div>
      )}

      {currentStep.type === 'question' && (
        <div className="flex flex-col items-center space-y-8">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
            {t(currentStep.description || '') as string}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center">
            <SecondaryButton
              text={t(currentStep.secondaryButton || 'global.skip') as string}
              fn={() => handleAnswer("skip")}
              actionName="onboarding_skip"
            />
            <PrimaryButton
              tone="brand"
              text={t(currentStep.primaryButton || '') as string}
              fn={() => handleAnswer("yes")}
              actionName="onboarding_yes_interested"
            />
          </div>
          <OnboardingLanguageSwitcher />
        </div>
      )}

      {currentStep.type === 'single-choice' && (
        <div className="flex flex-col items-center space-y-8">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
            {t(currentStep.description || '') as string}
          </p>
          <div className="w-full max-w-2xl grid grid-cols-1 gap-3">
            {choiceOptions?.map((option) => {
              const isSelected = selectedSingle === option.value;
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => setSelectedSingle(option.value)}
                  className={`text-left p-4 rounded-xl border-2 transition-all duration-200 ${onboardingChoiceSurface(isSelected)}`}
                >
                  <span className="text-foreground font-medium">
                    {t(option.label || '') as string}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-col items-center gap-4 w-full max-w-md">
            <PrimaryButton
              tone="brand"
              text={t(currentStep.primaryButton || '') as string}
              fn={handleNext}
              actionName="onboarding_first_win_next"
              isDisabled={!selectedSingle}
            />
            <SecondaryButton
              text={t(currentStep.secondaryButton || 'global.skip') as string}
              fn={() => handleAnswer("skip")}
              actionName="onboarding_first_win_skip"
            />
          </div>
          <OnboardingLanguageSwitcher />
        </div>
      )}

      {currentStep.type === 'multiple-choice' && (
        <div className="flex flex-col items-center space-y-8">
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl text-center">
            {t(currentStep.description || '') as string}
          </p>
          
          <div className="w-full max-w-2xl space-y-3">
            {choiceOptions?.map((option) => (
              <label 
                key={option.value} 
                className={`flex items-center p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${onboardingChoiceSurface(selectedOptions.includes(option.value))}`}
              >
                <input
                  type="checkbox"
                  value={option.value}
                  checked={selectedOptions.includes(option.value)}
                  onChange={() => handleOptionChange(option.value)}
                  className={`w-5 h-5 ${ONBOARDING_BRAND.accentControl} rounded focus:ring-2 cursor-pointer`}
                />
                <span className={`ml-3 text-foreground ${ONBOARDING_BRAND.optionLabelHover} transition-colors duration-200`}>
                  {t(option.label || '') as string}
                </span>
              </label>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center">
            <SecondaryButton
              text={t(currentStep.secondaryButton || 'global.skip') as string}
              fn={() => handleAnswer("skip")}
              actionName="onboarding_skip"
            />
            <PrimaryButton
              tone="brand"
              text={t(currentStep.primaryButton || '') as string}
              fn={handleNext}
              actionName="onboarding_multiple_choice_next"
              isDisabled={selectedOptions.length === 0}
            />
          </div>
          <OnboardingLanguageSwitcher />
        </div>
      )}
    </OnboardingWizardLayout>
  );
};

export default OnboardingFlow;
